// OnboardingScreen.js - Main onboarding flow container
// Supports both student and teacher onboarding flows
import React, { useCallback } from 'react';
import { View, Text, StyleSheet, SafeAreaView, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useTranslation } from '../../i18n/useTranslation';

// Student steps - Individual profile fields
import WelcomeStep from './WelcomeStep';
import GradeStep from './GradeStep';
import AgeStep from './AgeStep';
import PassionStep from './PassionStep';
import FavoriteSubjectStep from './FavoriteSubjectStep';
import ExtracurricularStep from './ExtracurricularStep';
import LearningStyleStep from './LearningStyleStep';
import BoardStep from './BoardStep';
import CompleteStep from './CompleteStep';

// Teacher steps
import TeacherWelcomeStep from './TeacherWelcomeStep';
import TeacherFeaturesStep from './TeacherFeaturesStep';
import TeacherCompleteStep from './TeacherCompleteStep';

const STUDENT_STEPS = 9; // Welcome, Grade, Age, Passion, FavSubject, Extracurricular, LearningStyle, Board, Complete
const TEACHER_STEPS = 3;

const OnboardingScreen = ({
  userName,
  isTeacher = false,
  onComplete,
  onCreateTest, // Teacher-specific callback
}) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const {
    currentStep,
    nextStep,
    prevStep,
    profile,
    board,
    updateProfile,
    updateBoard,
    completeOnboarding,
    skipOnboarding,
  } = useOnboarding();

  const totalSteps = isTeacher ? TEACHER_STEPS : STUDENT_STEPS;

  const colors = {
    bg: isDark ? '#0f172a' : '#f1f5f9',
    header: isDark ? '#1e293b' : '#1e3a5f',
    text: '#ffffff',
    dotActive: '#ffffff',
    dotInactive: 'rgba(255, 255, 255, 0.4)',
  };

  const handleSkip = useCallback(async () => {
    await skipOnboarding();
    onComplete?.();
  }, [skipOnboarding, onComplete]);

  const handleComplete = useCallback(async () => {
    await completeOnboarding();
    onComplete?.();
  }, [completeOnboarding, onComplete]);

  const handleCreateTest = useCallback(async () => {
    await completeOnboarding();
    onCreateTest?.();
  }, [completeOnboarding, onCreateTest]);

  // Helper to update a single profile field
  const updateProfileField = useCallback((field, value) => {
    updateProfile({ ...profile, [field]: value });
  }, [profile, updateProfile]);

  const stepLabel = t('STEP {current} of {total}')
    .replace('{current}', String(currentStep + 1))
    .replace('{total}', String(totalSteps));

  const renderProgressDots = () => {
    // For many steps, show a progress bar instead of dots
    if (totalSteps > 6) {
      const progress = ((currentStep + 1) / totalSteps) * 100;
      return (
        <View style={styles.progressContainer}>
          <Text style={[styles.stepText, { color: colors.text }]}>
            {stepLabel}
          </Text>
          <View style={styles.progressBarContainer}>
            <View style={[styles.progressBar, { width: `${progress}%` }]} />
          </View>
        </View>
      );
    }

    return (
      <View style={styles.progressContainer}>
        <Text style={[styles.stepText, { color: colors.text }]}>
          {stepLabel}
        </Text>
        <View style={styles.dotsRow}>
          {Array.from({ length: totalSteps }).map((_, index) => (
            <View
              key={index}
              style={[
                styles.dot,
                {
                  backgroundColor:
                    index <= currentStep ? colors.dotActive : colors.dotInactive,
                },
              ]}
            />
          ))}
        </View>
      </View>
    );
  };

  // Student onboarding flow - 9 steps
  const renderStudentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <WelcomeStep
            userName={userName}
            onNext={nextStep}
            onSkip={handleSkip}
          />
        );
      case 1:
        return (
          <GradeStep
            value={profile?.grade || ''}
            onChange={(value) => updateProfileField('grade', value)}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 2:
        return (
          <AgeStep
            value={profile?.age || ''}
            onChange={(value) => updateProfileField('age', value)}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <PassionStep
            value={profile?.passion || ''}
            onChange={(value) => updateProfileField('passion', value)}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 4:
        return (
          <FavoriteSubjectStep
            value={profile?.favorite_stem_subject || ''}
            onChange={(value) => updateProfileField('favorite_stem_subject', value)}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 5:
        return (
          <ExtracurricularStep
            value={profile?.extracurricular_activity || ''}
            onChange={(value) => updateProfileField('extracurricular_activity', value)}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 6:
        return (
          <LearningStyleStep
            value={profile?.preferred_learning_style || 'visual'}
            onChange={(value) => updateProfileField('preferred_learning_style', value)}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 7:
        return (
          <BoardStep
            selectedBoard={board}
            onBoardChange={updateBoard}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 8:
        return (
          <CompleteStep
            userName={userName}
            onStartLearning={handleComplete}
          />
        );
      default:
        return null;
    }
  };

  // Teacher onboarding flow
  const renderTeacherStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <TeacherWelcomeStep
            userName={userName}
            onNext={nextStep}
            onSkip={handleSkip}
          />
        );
      case 1:
        return (
          <TeacherFeaturesStep
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 2:
        return (
          <TeacherCompleteStep
            userName={userName}
            onGoToDashboard={handleComplete}
            onCreateFirstTest={handleCreateTest}
          />
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.bg }]}>
      {/* Header with progress */}
      <View style={[styles.header, { backgroundColor: colors.header }]}>
        <Text style={styles.logo}>3D AI Tutor</Text>
        {isTeacher && (
          <Text style={styles.roleLabel}>{t('For Educators')}</Text>
        )}
        {renderProgressDots()}
      </View>

      {/* Step content */}
      <View style={styles.stepContainer}>
        {isTeacher ? renderTeacherStep() : renderStudentStep()}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingTop: Platform.OS === 'web' ? 16 : 8,
    paddingBottom: 20,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  logo: {
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 4,
  },
  roleLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  progressContainer: {
    alignItems: 'center',
    width: '100%',
  },
  stepText: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
    marginBottom: 10,
  },
  dotsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  progressBarContainer: {
    width: '60%',
    maxWidth: 200,
    height: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#fff',
    borderRadius: 3,
  },
  stepContainer: {
    flex: 1,
  },
});

export default OnboardingScreen;
